import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context/src/app-context';

import {
  AlertTypes,
  Errors,
} from 'src/areas/product-alert/components/product-alert-form';
import { createAlertRequest } from 'src/areas/sidebar/actions/alert';
import { isValidEmail } from 'src/areas/account/helpers';
import { StockStatusAlertModal } from './stock-status-alert-modal';

import { AppState } from 'src/reducers';

import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import { Category } from 'src/types';
import { IProductTabMainProps } from 'src/areas/product/components/product-tabs/types';

import {
  StyledAlertButton,
  StyledCategoryText,
  StyledContainer,
  StyledContent,
  StyledIllustration,
  StyledLink,
  StyledTitle,
} from './styles';

type Props = {
  category?: Category | null;
  product: IProductTabMainProps['product'];
};

export const NoStockStatus = (props: Props) => {
  const { category, product } = props;

  const { market, t } = useAppContext();

  const dispatch = useDispatch();

  const isAuthenticated = useIsAuthenticated();

  const [modalOpen, setModalOpen] = useState(false);

  const userProductAlerts: { productId: number; alertId: number }[] =
    useSelector((state: AppState) => state.areas.sidebar.alerts.products) ?? [];

  const submitAlert = (
    event: React.FormEvent,
    email?: string,
    onError?: (error?: Errors) => void
  ) => {
    event.preventDefault();
    if (!isAuthenticated && email && !isValidEmail(email) && onError)
      return onError(Errors.ERROR_EMAIL_INVALID);

    const userAlert = userProductAlerts.find(
      alert => alert.productId === product.id
    );

    const stockStatusAlert = {
      alertId: userAlert?.alertId,
      email: !isAuthenticated ? email : undefined,
      productId: product.id,
      type: AlertTypes.IN_STOCK,
    };

    return dispatch(createAlertRequest(stockStatusAlert));
  };

  const handleAlertClick = (event: React.FormEvent) => {
    if (!isAuthenticated) return setModalOpen(true);

    return submitAlert(event);
  };

  const isPreRelease =
    product.releaseDate &&
    Date.parse(product.releaseDate) > new Date().getTime();

  const getNoStockStatusCopy = () => {
    // The product has no offers in stock
    if (product.prices?.nodes?.length) {
      return {
        title: t('product', 'prices.not-in-stock'),
        buttonText: t('product', 'prices.not-in-stock-push'),
        showCategoryLink: false,
      };
    }

    // The product is new on pre-release and has no offers yet
    if (isPreRelease) {
      return {
        title: `${t('product', 'pre-release.new-release')}. ${t(
          'product',
          'pre-release.comming-soon'
        )}`,
        buttonText: t('product', 'pre-release.stock-alert-button'),
        showCategoryLink: false,
      };
    }

    // The product has no offers at all
    return {
      title: t('product', 'prices.no-prices'),
      showCategoryLink: true,
    };
  };

  const stockStatusCopy = getNoStockStatusCopy();

  return (
    <>
      <StockStatusAlertModal
        closeModal={() => setModalOpen(false)}
        isOpen={modalOpen}
        onSubmit={submitAlert}
      />

      <StyledContainer>
        <StyledIllustration
          alt=""
          src={`${market.cdn}/g/rfe/icons/no-offers-illustration.svg`}
        />
        <StyledContent>
          <StyledTitle>{stockStatusCopy.title}</StyledTitle>
          {stockStatusCopy.showCategoryLink && category && (
            <div>
              <StyledCategoryText>
                {t('product', 'prices.view-all-in-category-link')}
              </StyledCategoryText>
              <StyledLink to={category.pathName}>{category.name}</StyledLink>
            </div>
          )}
          {stockStatusCopy.buttonText && (
            <StyledAlertButton onClick={handleAlertClick}>
              {stockStatusCopy.buttonText}
            </StyledAlertButton>
          )}
        </StyledContent>
      </StyledContainer>
    </>
  );
};
